/**
 * Centralized localStorage utility functions with error handling
 * Provides consistent error handling and testability for localStorage operations
 */

/**
 * Safely get an item from localStorage
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist or error occurs
 * @returns {*} The stored value or defaultValue
 */
export const getItem = (key, defaultValue = null) => {
  try {
    if (typeof global.localStorage === 'undefined') return defaultValue;
    const value = global.localStorage.getItem(key);
    return value !== null ? value : defaultValue;
  } catch (_unused) {
    return defaultValue;
  }
};

/**
 * Safely set an item in localStorage
 * @param {string} key - The key to set
 * @param {string} value - The value to store
 * @returns {boolean} True if successful, false otherwise
 */
export const setItem = (key, value) => {
  try {
    if (typeof global.localStorage === 'undefined') return false;
    global.localStorage.setItem(key, value);
    return true;
  } catch (_unused2) {
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @param {string} key - The key to remove
 * @returns {boolean} True if successful, false otherwise
 */
export const removeItem = key => {
  try {
    if (typeof global.localStorage === 'undefined') return false;
    global.localStorage.removeItem(key);
    return true;
  } catch (_unused3) {
    return false;
  }
};

/**
 * Safely get a JSON item from localStorage
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist or error occurs
 * @returns {*} The parsed JSON value or defaultValue
 */
export const getJSONItem = (key, defaultValue = null) => {
  try {
    const value = getItem(key);
    if (value === null) return defaultValue;
    return JSON.parse(value);
  } catch (_unused4) {
    return defaultValue;
  }
};

/**
 * Safely set a JSON item in localStorage
 * @param {string} key - The key to set
 * @param {*} value - The value to store (will be JSON stringified)
 * @returns {boolean} True if successful, false otherwise
 */
export const setJSONItem = (key, value) => {
  try {
    const jsonString = JSON.stringify(value);
    return setItem(key, jsonString);
  } catch (_unused5) {
    return false;
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available, false otherwise
 */
export const isAvailable = () => {
  try {
    return typeof global.localStorage !== 'undefined';
  } catch (_unused6) {
    return false;
  }
};